<template>
  <div class="tasks-filter">
    <DatesFilter v-model="datesValue" />
    <StatusFilter
      v-model="statusValue"
      :status-list="normalizedStatusList"
    />
    <TypeFilter
      v-model="typesValue"
      :types-list="normalizedTypesList"
    />
    <ManageTypeFilter
      v-model="manageTypesValue"
      :manage-type-list="normalizedManageTypeList"
    />
    <NameFilter v-model="nameValue" />
    <UserFilter
      v-model="usernameValue"
      :users-list="normalizedUsersList"
    />
    <ActiveFilter v-model="showActiveOnlyValue" />
    <DeletedFilter v-model="showDeletedValue" />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import DatesFilter from '@/components/Apps/TaskFilters/DatesFilter.vue';
import ActiveFilter from '@/components/Apps/TaskFilters/ActiveFilter.vue';
import DeletedFilter from '@/components/Apps/TaskFilters/DeletedFilter.vue';
import ManageTypeFilter from '@/components/Apps/TaskFilters/ManageTypeFilter.vue';
import NameFilter from '@/components/Apps/TaskFilters/NameFilter.vue';
import StatusFilter from '@/components/Apps/TaskFilters/StatusFilter.vue';
import TypeFilter from '@/components/Apps/TaskFilters/TypeFilter.vue';
import UserFilter from '@/components/Apps/TaskFilters/UserFilter.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      startDate: '',
      endDate: '',
      showActiveOnly: false,
      showDeleted: false,
      dynamicFilters: {},
    }),
  },
  statusList: {
    type: Array,
    default: () => [],
  },
  usersList: {
    type: Array,
    default: () => [],
  },
  typesList: {
    type: Array,
    default: () => [],
  },
  manageTypeList: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const normalizeOptions = (options = []) => options.map((option) => {
  if (typeof option === 'string') {
    return { key: option, value: option };
  }
  return {
    key: option.key ?? option.label ?? option.value,
    value: option.value ?? option.key ?? option.label,
  };
});

const normalizedStatusList = computed(() => normalizeOptions(props.statusList));
const normalizedUsersList = computed(() => normalizeOptions(props.usersList));
const normalizedTypesList = computed(() => normalizeOptions(props.typesList));
const normalizedManageTypeList = computed(() => normalizeOptions(props.manageTypeList));

const emitUpdate = (patch) => {
  const current = props.modelValue || {};
  emit('update:modelValue', {
    ...current,
    ...patch,
    dynamicFilters: { ...(current.dynamicFilters || {}), ...(patch.dynamicFilters || {}) },
  });
};

const emitDynamic = (key, value) => {
  emitUpdate({ dynamicFilters: { [key]: value } });
};

const dynamicField = (key, defaultValue) => computed({
  get() {
    const value = props.modelValue?.dynamicFilters?.[key];
    if (value === undefined || value === null) return defaultValue;
    return value;
  },
  set(value) {
    emitDynamic(key, value);
  },
});

const statusValue = dynamicField('status', []);
const typesValue = dynamicField('types', []);
const manageTypesValue = dynamicField('manage_types', []);
const usernameValue = dynamicField('username', []);
const nameValue = dynamicField('name', '');

const datesValue = computed({
  get() {
    return {
      startDate: props.modelValue?.startDate || '',
      endDate: props.modelValue?.endDate || '',
    };
  },
  set(value) {
    emitUpdate({
      startDate: value?.startDate || '',
      endDate: value?.endDate || '',
    });
  },
});

const showDeletedValue = computed({
  get() {
    return Boolean(props.modelValue?.showDeleted);
  },
  set(value) {
    emitUpdate({ showDeleted: Boolean(value) });
  },
});

const showActiveOnlyValue = computed({
  get() {
    return Boolean(props.modelValue?.showActiveOnly);
  },
  set(value) {
    emitUpdate({ showActiveOnly: Boolean(value) });
  },
});
</script>

<style lang="scss" scoped>
.tasks-filter {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
